import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Grid, Stack, TextField, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { mediumMaterialIconSx } from '../../styles/iconStyles';
import { useUserProfile } from './userProfile.service';
import { PHONE_MAX_LENGTH } from '../../constant/phone';
import { Authentication } from '../../../service/auth.utils';
import NotificationSnackbar from '../../components/notificationSnackbar';
import { appendAzureSasToken } from '../../../service/azure.utils';
import { personaLabel } from '../../constant/personaLabel';

const pageCardSx = {
  border: '1px solid #DFE5F6',
  borderRadius: '24px',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 8px 24px rgba(31, 46, 85, 0.06)',
};

const detailCardSx = {
  border: '1px solid #DFE5F6',
  borderRadius: '16px',
  backgroundColor: '#FFFFFF',
  p: 2,
  minHeight: 92,
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E4E6F1' },
    '&:hover fieldset': { borderColor: '#E4E6F1' },
    '&.Mui-focused fieldset': { borderColor: '#294190' },
  },
  '& .MuiOutlinedInput-input': {
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    color: '#1C1B1F',
    fontWeight: 400,
    px: 1.75,
    py: 1.5,
    '&::placeholder': {
      color: '#625B71',
      opacity: 1,
    },
  },
};

function DetailField({ label, value, editing = false, onChange, placeholder }) {
  return (
    <Box sx={detailCardSx}>
      <Typography sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', fontWeight: 500, color: '#1C1B1F', mb: 1 }}>
        {label}
      </Typography>
      {editing ? (
        <TextField
          value={value || ''}
          onChange={onChange}
          fullWidth
          placeholder={placeholder || `Enter ${label.toLowerCase()}`}
          sx={inputSx}
        />
      ) : (
        <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
          {value || '-'}
        </Typography>
      )}
    </Box>
  );
}

// Empty stays empty (not '-'): the edit field below is fed through this too.
function formatPersonaTypeName(typeName) {
  return typeName ? personaLabel(typeName) : typeName;
}

function getStoredUserData() {
  try {
    const raw = localStorage.getItem('userData');
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function formatApiUserData(apiData) {
  if (!apiData) return null;

  const nameParts = (apiData.fullName || '').split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return {
    id: apiData.id,
    firstName,
    lastName,
    email: apiData.email,
    phoneNumber: apiData.phoneNumber,
    countryCode: apiData.countryCode,
    persona: apiData.persona,
    status: apiData.status,
    profileImage: appendAzureSasToken(apiData.profileImageUrl),
    userType: {
      typeName: apiData.persona || '',
      shortName: apiData.persona ? apiData.persona.substring(0, 2).toUpperCase() : '',
    },
  };
}

function notifyUserDataUpdated() {
  window.dispatchEvent(new Event('user-data-updated'));
}

export default function UserProfile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { fetchUserProfile, updateUserProfileData, uploadImage, deleteImage } = useUserProfile();
  const [userData, setUserData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setLoading(true);

        // Check if user is authenticated
        const auth = new Authentication();
        if (!auth.isAuthenticated()) {
          setError('You are not authenticated. Please log in first.');
          navigate('/auth/login');
          return;
        }

        const apiData = await fetchUserProfile();
        const formattedData = formatApiUserData(apiData);
        setUserData(formattedData);
        setDraftData(formattedData);
        localStorage.setItem('userData', JSON.stringify(formattedData));
        setError(null);
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        const storedData = getStoredUserData();

        if (storedData) {
          // Use cached data if available
          setUserData(storedData);
          setDraftData(storedData);
          setError('Failed to load fresh profile. Showing cached data.');
        } else {
          setUserData(null);
          setDraftData(null);
          setError(err.message || err.statusCode?.toString() || 'Failed to load profile');
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [fetchUserProfile, navigate]);

  const fullName = useMemo(() => {
    const source = editMode ? draftData : userData;
    if (!source) {
      return '';
    }

    return [source.firstName, source.lastName].filter(Boolean).join(' ');
  }, [draftData, editMode, userData]);

  const avatarInitials = useMemo(() => {
    const parts = fullName.split(' ').filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'U';
  }, [fullName]);

  const saveUserData = (nextValue) => {
    setUserData(nextValue);
    setDraftData(nextValue);
    localStorage.setItem('userData', JSON.stringify(nextValue));
    notifyUserDataUpdated();
  };

  const updateDraftField = (field, value) => {
    setDraftData((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const updateDraftUserTypeField = (field, value) => {
    setDraftData((prev) => ({
      ...(prev || {}),
      userType: {
        ...(prev?.userType || {}),
        [field]: value,
      },
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload a valid image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size should be 5 MB or less.');
      return;
    }

    try {
      setImageUploading(true);
      setUploadError('');
      setImageLoadError(false);

      // Upload to API
      const response = await uploadImage(file);
      const formattedData = formatApiUserData(response);

      // Update both userData and draftData with the new profile image URL
      saveUserData(formattedData);
      setDraftData(formattedData);

      setSaveSuccess(true);
      setSaveMessage('Profile image uploaded successfully');
    } catch (err) {
      console.error('Failed to upload image:', err);
      setUploadError(err.message || 'Failed to upload image');
      setSaveSuccess(false);
      setSaveMessage(err.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = async () => {
    try {
      setImageUploading(true);
      setUploadError('');

      // Call API to delete image from Azure
      await deleteImage();

      // Update local state
      const nextValue = {
        ...(userData || {}),
        profileImage: null,
      };
      saveUserData(nextValue);
      setDraftData(nextValue);
      setImageLoadError(false);

      setSaveMessage('Profile image removed');
      setSaveSuccess(true);
    } catch (err) {
      console.error('Failed to remove image:', err);
      setUploadError(err.message || 'Failed to remove image');
      setSaveSuccess(false);
      setSaveMessage(err.message || 'Failed to remove image');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEditToggle = () => {
    setDraftData(userData);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setDraftData(userData);
    setEditMode(false);
  };

  const handleSaveProfile = async () => {
    try {
      setSaveLoading(true);
      setSaveMessage(null);

      const updateData = {
        fullName: draftData?.firstName && draftData?.lastName
          ? `${draftData.firstName} ${draftData.lastName}`.trim()
          : draftData?.firstName || '',
        phoneNumber: draftData?.phoneNumber || null,
        countryCode: draftData?.countryCode || null,
      };

      await updateUserProfileData(updateData);

      // Refetch complete profile data after successful update
      const updatedProfileData = await fetchUserProfile();
      const formattedData = formatApiUserData(updatedProfileData);

      saveUserData(formattedData);
      setEditMode(false);
      setSaveSuccess(true);
      setSaveMessage('Profile updated successfully');
    } catch (err) {
      console.error('Failed to save profile:', err);
      setSaveSuccess(false);
      setSaveMessage(err.message || 'Failed to save profile');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !userData) {
    return (
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1360, xl: 1280 }, boxSizing: 'border-box', mx: 'auto', px: { xs: 0, lg: 3, xl: 4 } }}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<span className="material-symbols-rounded">arrow_back</span>}
          sx={{
            minWidth: 0,
            mb: 2.5,
            px: 0.5,
            color: '#1C1B1F',
            textTransform: 'none',
            fontSize: 16,
            lineHeight: '24px',
            letterSpacing: '0.15px',
            fontWeight: 500,
            '& .material-symbols-rounded': { fontSize: 20 },
          }}
        >
          Back
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Failed to load profile</Typography>
          <Typography sx={{ fontSize: 14 }}>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1360, xl: 1280 }, boxSizing: 'border-box', mx: 'auto', px: { xs: 0, lg: 3, xl: 4 } }}>
      <Button
        onClick={() => navigate(-1)}
        startIcon={<span className="material-symbols-rounded">arrow_back</span>}
        sx={{
          minWidth: 0,
          mb: 2.5,
          px: 0.5,
          color: '#1C1B1F',
          textTransform: 'none',
          fontSize: 16,
          lineHeight: '24px',
          letterSpacing: '0.15px',
          fontWeight: 500,
          '& .material-symbols-rounded': { fontSize: 20 },
        }}
      >
        Back
      </Button>

      <Typography className="app-page-title" sx={{ fontSize: '34px', lineHeight: '40px', letterSpacing: '-0.25px', fontWeight: 700, color: '#1C1B1F', mb: 1 }}>
        User Profile
      </Typography>
      <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F', mb: 4 }}>
        Review your profile information and keep your profile picture up to date.
      </Typography>

      {error && userData && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ ...pageCardSx, p: { xs: 2.5, sm: 4 }, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 3.5 }}>
            <Stack alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
              <Box
                sx={{
                  position: 'relative',
                  width: 148,
                  height: 148,
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={{
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    border: '1px solid #DFE5F6',
                    background: 'linear-gradient(180deg, #F7F7F7 0%, #ECECEC 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#625B71',
                    fontSize: 40,
                    lineHeight: '48px',
                    fontWeight: 600,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {(editMode ? draftData?.profileImage : userData?.profileImage) && !imageLoadError ? (
                    <Box
                      component="img"
                      src={editMode ? draftData?.profileImage : userData?.profileImage}
                      alt="Profile"
                      onError={() => setImageLoadError(true)}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%',
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      {avatarInitials}
                    </Box>
                  )}
                </Box>
                <Button
                  onClick={() => {
                    const hasImage = editMode ? draftData?.profileImage : userData?.profileImage;
                    if (hasImage) {
                      removeImage();
                    } else {
                      fileInputRef.current?.click();
                    }
                  }}
                  disabled={imageUploading}
                  sx={{
                    minWidth: 0,
                    width: 42,
                    height: 42,
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    zIndex: 2,
                    borderRadius: '50%',
                    border: '2px solid #FFFFFF',
                    bgcolor: (editMode ? draftData?.profileImage : userData?.profileImage) ? '#FDE0DC' : '#4F8506',
                    color: (editMode ? draftData?.profileImage : userData?.profileImage) ? '#DA342E' : '#FFFFFF',
                    boxShadow: '0px 6px 18px rgba(31, 46, 85, 0.18)',
                    p: 0,
                    '&:hover': {
                      bgcolor: (editMode ? draftData?.profileImage : userData?.profileImage) ? '#FDE0DC' : '#4F8506',
                    },
                    '&:disabled': {
                      bgcolor: '#cccccc',
                      color: '#666666',
                    },
                  }}
                >
                  <Box component="span" className="material-symbols-rounded" sx={mediumMaterialIconSx}>
                    {imageUploading ? 'hourglass_empty' : ((editMode ? draftData?.profileImage : userData?.profileImage) ? 'delete' : 'upload')}
                  </Box>
                </Button>
              </Box>

              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleImageUpload}
              />
              {!!uploadError && (
                <Typography sx={{ fontSize: 12, lineHeight: '16px', letterSpacing: '0.4px', color: '#DA342E' }}>
                  {uploadError}
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 8.5 }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 28, lineHeight: '36px', letterSpacing: '-0.15px', fontWeight: 700, color: '#1C1B1F' }}>
                  {fullName || 'User'}
                </Typography>
                {!editMode ? (
                  <Button
                    onClick={handleEditToggle}
                    startIcon={<span className="material-symbols-rounded">edit</span>}
                    sx={{
                      minWidth: 110,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: '#DFE5F6',
                      color: '#294190',
                      textTransform: 'none',
                      fontSize: 16,
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                      fontWeight: 500,
                      '&:hover': { bgcolor: '#DFE5F6' },
                    }}
                  >
                    Edit
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1.25}>
                    <Button
                      onClick={handleCancelEdit}
                      disabled={saveLoading}
                      sx={{
                        minWidth: 96,
                        height: 40,
                        borderRadius: '10px',
                        border: '1px solid #294190',
                        color: '#294190',
                        textTransform: 'none',
                        fontSize: 16,
                        lineHeight: '24px',
                        letterSpacing: '0.15px',
                        fontWeight: 500,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveProfile}
                      disabled={saveLoading}
                      sx={{
                        minWidth: 96,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: '#294190',
                        color: '#FFFFFF',
                        textTransform: 'none',
                        fontSize: 16,
                        lineHeight: '24px',
                        letterSpacing: '0.15px',
                        fontWeight: 500,
                        '&:hover': { bgcolor: '#294190' },
                        '&:disabled': { bgcolor: '#cccccc', color: '#666666' },
                      }}
                    >
                      {saveLoading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                  </Stack>
                )}
              </Box>
              <Typography sx={{ fontSize: 28, lineHeight: '36px', letterSpacing: '-0.15px', fontWeight: 700, color: '#1C1B1F', display: 'none' }}>
                {fullName || 'User'}
              </Typography>
              <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#625B71' }}>
                {(editMode ? draftData?.email : userData?.email) || '-'}
              </Typography>
              <Stack direction="row" spacing={1.25} flexWrap="wrap">
                <Box sx={{ px: 1.5, py: 0.75, borderRadius: '999px', backgroundColor: '#EEF3FF', color: '#294190', fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', fontWeight: 500 }}>
                  {formatPersonaTypeName((editMode ? draftData?.userType?.typeName : userData?.userType?.typeName) || 'User Type')}
                </Box>
                <Box sx={{ px: 1.5, py: 0.75, borderRadius: '999px', backgroundColor: '#F1F1F1', color: '#1C1B1F', fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', fontWeight: 500 }}>
                  {(editMode ? draftData?.userType?.shortName : userData?.userType?.shortName) || 'Short Name'}
                </Box>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailField label="First Name" value={editMode ? draftData?.firstName : userData?.firstName} editing={editMode} onChange={(event) => updateDraftField('firstName', event.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailField label="Last Name" value={editMode ? draftData?.lastName : userData?.lastName} editing={editMode} onChange={(event) => updateDraftField('lastName', event.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailField label="Email" value={editMode ? draftData?.email : userData?.email} editing={editMode} onChange={(event) => updateDraftField('email', event.target.value)} placeholder="Enter email address" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailField
            label="Country Code"
            value={editMode ? draftData?.countryCode : userData?.countryCode}
            editing={editMode}
            onChange={(event) => updateDraftField('countryCode', event.target.value)}
            placeholder="e.g., +91"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailField
            label="Phone Number"
            value={editMode ? draftData?.phoneNumber : userData?.phoneNumber}
            editing={editMode}
            onChange={(event) => updateDraftField('phoneNumber', event.target.value.replace(/\D/g, '').slice(0, PHONE_MAX_LENGTH))}
            placeholder="Enter phone number"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailField
            label="User Type"
            value={formatPersonaTypeName(editMode ? draftData?.userType?.typeName : userData?.userType?.typeName)}
            editing={editMode}
            onChange={(event) => updateDraftUserTypeField('typeName', event.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailField
            label="Short Name"
            value={editMode ? draftData?.userType?.shortName : userData?.userType?.shortName}
            editing={editMode}
            onChange={(event) => updateDraftUserTypeField('shortName', event.target.value)}
          />
        </Grid>
      </Grid>

      <NotificationSnackbar
        open={!!saveMessage}
        onClose={() => setSaveMessage(null)}
        message={saveMessage}
        severity={saveSuccess ? 'success' : 'error'}
        autoHideDuration={6000}
      />
    </Box>
  );
}
